import { useState } from 'react';

interface UsePromptEnhanceReturn {
  enhance: (prompt: string) => Promise<void>;
  isEnhancing: boolean;
  enhancedPrompt: string | null;
  error: string | null;
  clear: () => void;
}

/**
 * Hook for enhancing simple prompts into detailed, professional Remotion prompts
 */
export function usePromptEnhance(): UsePromptEnhanceReturn {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedPrompt, setEnhancedPrompt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enhance = async (prompt: string) => {
    if (!prompt.trim() || prompt.length < 3) {
      setError('Prompt too short to enhance');
      return;
    }

    setIsEnhancing(true);
    setError(null);
    setEnhancedPrompt(null);

    try {
      const response = await fetch('/api/enhance-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setEnhancedPrompt(data.enhancedPrompt);
      } else {
        setError(data.error || 'Enhancement failed');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsEnhancing(false);
    }
  };

  const clear = () => {
    setEnhancedPrompt(null);
    setError(null);
  };

  return { enhance, isEnhancing, enhancedPrompt, error, clear };
}
